/**
 * Age-group pitch dimensions for the drill canvas.
 *
 * Sources:
 *   - FA Youth Football Standards (England)
 *   - US Soccer Player Development Initiatives
 *
 * Markings (penalty area, goal area, goal size) follow each age group's
 * regulations. Drills shown on the canvas render at the right scale
 * relative to the players they'll be run on.
 */

export type PitchFormat = '5v5' | '7v7' | '9v9' | '11v11_youth' | '11v11_senior';

export interface PitchSpec {
  format: PitchFormat;
  /** Friendly label rendered in the topbar. */
  label: string;
  /** Length goal-to-goal in metres. */
  length: number;
  /** Width sideline-to-sideline in metres. */
  width: number;
  /** Penalty area depth (out from goal-line) in metres. */
  penaltyAreaLength: number;
  /** Penalty area width (cross-pitch) in metres. */
  penaltyAreaWidth: number;
  /** Goal area / 6-yard box depth. 0 when the format doesn't use one. */
  goalAreaLength: number;
  goalAreaWidth: number;
  /** Goal mouth width — sideline-to-sideline along the goal-line. */
  goalWidth: number;
  /** Centre circle radius. */
  centreCircleRadius: number;
  /** Penalty spot distance from goal-line. */
  penaltySpotDistance: number;
}

/** 5v5 — U7 / U8. ~30×20m pitch, no penalty area, small goals. */
export const PITCH_5V5: PitchSpec = {
  format: '5v5',
  label: '5v5 · U7-U8',
  length: 36.6,
  width: 27.4,
  penaltyAreaLength: 7,
  penaltyAreaWidth: 14,
  goalAreaLength: 0,
  goalAreaWidth: 0,
  goalWidth: 3.66,
  centreCircleRadius: 5.5,
  penaltySpotDistance: 7,
};

/** 7v7 — U9 / U10. ~55×37m pitch. */
export const PITCH_7V7: PitchSpec = {
  format: '7v7',
  label: '7v7 · U9-U10',
  length: 54.9,
  width: 36.6,
  penaltyAreaLength: 8.2,
  penaltyAreaWidth: 16.5,
  goalAreaLength: 0,
  goalAreaWidth: 0,
  goalWidth: 3.66,
  centreCircleRadius: 6.5,
  penaltySpotDistance: 7,
};

/** 9v9 — U11 / U12. ~73×46m pitch with a small 6-yard box. */
export const PITCH_9V9: PitchSpec = {
  format: '9v9',
  label: '9v9 · U11-U12',
  length: 73.2,
  width: 45.7,
  penaltyAreaLength: 12.8,
  penaltyAreaWidth: 27.4,
  goalAreaLength: 4.6,
  goalAreaWidth: 13.7,
  goalWidth: 4.88,
  centreCircleRadius: 7.5,
  penaltySpotDistance: 9,
};

/** 11v11 youth — U13 to U17. ~91×55m pitch with full markings. */
export const PITCH_11V11_YOUTH: PitchSpec = {
  format: '11v11_youth',
  label: '11v11 · U13-U17',
  length: 91.4,
  width: 54.9,
  penaltyAreaLength: 16.5,
  penaltyAreaWidth: 40.3,
  goalAreaLength: 5.5,
  goalAreaWidth: 18.3,
  goalWidth: 7.32,
  centreCircleRadius: 9.15,
  penaltySpotDistance: 11,
};

/** 11v11 senior — U18+ / open age. FIFA spec 105×68m. */
export const PITCH_11V11_SENIOR: PitchSpec = {
  format: '11v11_senior',
  label: '11v11 · U18+',
  length: 105,
  width: 68,
  penaltyAreaLength: 16.5,
  penaltyAreaWidth: 40.3,
  goalAreaLength: 5.5,
  goalAreaWidth: 18.3,
  goalWidth: 7.32,
  centreCircleRadius: 9.15,
  penaltySpotDistance: 11,
};

export const PITCH_SPECS: PitchSpec[] = [
  PITCH_5V5,
  PITCH_7V7,
  PITCH_9V9,
  PITCH_11V11_YOUTH,
  PITCH_11V11_SENIOR,
];

/** Returns the appropriate PitchSpec for an FA age-group label
 *  ("U7", "U10", "U13", "U17", "U19" …). Falls back to 11v11_youth
 *  when the input is missing or unrecognised. */
export function pitchSpecForAgeGroup(ageGroup: string | null | undefined): PitchSpec {
  if (!ageGroup) return PITCH_11V11_YOUTH;
  const m = ageGroup.match(/U?(\d+)/i);
  const n = m ? Number(m[1]) : NaN;
  if (Number.isNaN(n)) return PITCH_11V11_YOUTH;
  if (n <= 8) return PITCH_5V5;
  if (n <= 10) return PITCH_7V7;
  if (n <= 12) return PITCH_9V9;
  if (n <= 17) return PITCH_11V11_YOUTH;
  return PITCH_11V11_SENIOR;
}

/** Padding around the touchlines in the SVG viewBox — gives arrow
 *  drawing a little room to start on the touchline without clipping. */
export const PITCH_PADDING_M = 2;
